import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { usuarioSchema } from './validation/usuarioSchema'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  showAlert,
  showError,
  OptionsHelper,
  ButtonType,
  GenderType,
  MaskType,
} from '_metronic/helpers'
import {
  Button,
  FetchState,
  FormDateInput,
  FormInput,
  FormSelect,
  FormTextArea,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from 'app/components'
import { UsuarioDtoUpdate, UsuarioView } from '../../core/_models'
import { updateUser, updateUsuarioGrupo, updateUsuarioPerfis } from '../../core/_requests'

export const UsuarioModal: FC = () => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: usuario } = (fetchStates['usuario'] || {}) as FetchState<UsuarioView>

  const initialValues = {
    ativo: usuario
      ? { label: usuario.ativo ? 'Ativo' : 'Inativo', value: usuario.ativo }
      : (undefined as IOption<boolean> | undefined),
    primeiroNome: usuario?.pessoa?.primeiroNome || '',
    sobreNome: usuario?.pessoa?.sobreNome || '',
    email: usuario?.email || '',
    cpf: usuario?.pessoa?.cpf || usuario?.cpf || '',
    telefone: usuario?.pessoa?.telefone || '',
    nascimento: usuario?.pessoa?.dataNascimento || '',
    sexo: usuario?.pessoa?.sexo
      ? {
          label: usuario.pessoa.sexo === GenderType.Feminino ? 'Feminino' : 'Masculino',
          value: usuario.pessoa.sexo,
        }
      : undefined,
    grupo: usuario?.grupo?.nome
      ? { label: `${usuario.grupo.descricao} - ${usuario.grupo.nome}`, value: usuario.grupo.nome }
      : undefined,
    perfis: (usuario?.grupo?.perfis || []).map((perfil) => ({
      label: perfil.descricao,
      value: perfil.nome,
    })) as IOption<string>[],
    justificativa: '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: useMemo(() => usuarioSchema(t, initialValues.cpf), [t, initialValues.cpf]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const data: UsuarioDtoUpdate = {
          id: Number(usuario?.id),
          ativo: values.ativo?.value as boolean,
          primeiroNome: values.primeiroNome,
          sobreNome: values.sobreNome,
          email: values.email,
          cpf: values.cpf || undefined,
          telefone: values.telefone || undefined,
          nascimento: values.nascimento || undefined,
          sexo: values.sexo?.value as GenderType,
          justificativa:
            values.cpf !== initialValues.cpf ? values.justificativa || undefined : undefined,
        }
        await updateUser(data)
        await updateUsuarioGrupo(Number(usuario?.id), { nome: values.grupo?.value as string })
        await updateUsuarioPerfis(Number(usuario?.id), {
          perfis: values.perfis.map((perfil) => perfil.value),
        })

        await showAlert({
          title: t('common.success'),
          text: t('alert.update', { name: t('usuario.label'), gender: t('usuario.gender') }),
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className='row'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='primeiroNome'
            entity='primeiroNome'
            className='mb-3'
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='sobreNome'
            entity='sobrenome'
            className='mb-3'
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='email' entity='email' className='mb-3' isRequired />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='telefone'
            entity='telefone'
            className='mb-3'
            mask={MaskType.Phone}
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='cpf'
            entity='cpf'
            className='mb-3'
            mask={MaskType.CPF}
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormDateInput
            formik={formik}
            name='nascimento'
            entity='data_nascimento'
            className='mb-3'
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            entity='sexo'
            name='sexo'
            options={OptionsHelper.generos}
            className='mb-3'
            isDisabled={formik.isSubmitting}
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            entity='grupo'
            name='grupo'
            fetchOptions={OptionsHelper.getOptionsGrupos}
            className='mb-3'
            isDisabled={formik.isSubmitting}
            isRequired
          />
        </div>
      </div>
      <FormSelect
        formik={formik}
        entity='situacao'
        name='ativo'
        options={OptionsHelper.statusCadastro}
        className='mb-3'
        isClearable={false}
        isDisabled={formik.isSubmitting}
        isRequired
      />
      <FormSelect
        formik={formik}
        entity='permissao'
        name='perfis'
        fetchOptions={OptionsHelper.getOptionsPerfis}
        isDisabled={formik.isSubmitting}
        className='mb-3'
        isRequired
        isMulti
      />

      {formik.values.cpf !== initialValues.cpf && (
        <FormTextArea
          formik={formik}
          name='justificativa'
          entity='justificativa_cpf'
          placeholder='Descreva o motivo da alteração do CPF'
          className='mb-3'
          isRequired
        />
      )}

      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>

        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
